import { IconButton, useTheme } from "@mui/material";
import { EllipsisIcon, FolderClosedIcon, Pencil } from "lucide-react";
import { type FC, type MouseEvent, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFolderEditMutation } from "web_myplann/Api/Document/useFolderEditMutation";
import type { DocumentTagResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { RenameFolderForm } from "web_myplann/Portfolio/Forms/RenameFolderForm/RenameFolderForm";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { ActionsDrawerRedesign } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRedesign";
import type { DrawerAction } from "web_myplann/Shared/Components/Redesign/ActionsDrawerRow";
import { RedesignListItemRow } from "web_myplann/Shared/Components/Redesign/RedesignListItemRow";

type Props = {
	item: DocumentTagResponseDTO;
};

export const FolderItemRedesign: FC<Props> = ({ item }) => {
	const theme = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const { mutate: renameFolder } = useFolderEditMutation();

	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);

	const actions: DrawerAction[] = [
		{
			key: "rename",
			label: t(Resources.Portfolio.List.FolderActionsDrawer.Actions.rename),
			icon: Pencil,
			onClick: () => setIsRenameDialogOpen(true),
		},
	];

	const handleOpenDrawer = (event: MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
		setIsDrawerOpen(true);
	};

	const handleRename = (title: string) => {
		renameFolder({
			requestContract: { titleToEdit: item.title, title },
		});
		setIsRenameDialogOpen(false);
		setIsDrawerOpen(false);
	};

	return (
		<>
			<RedesignListItemRow
				icon={
					<FolderClosedIcon
						size={theme.iconSize.md}
						color={theme.palette.grey[500]}
					/>
				}
				title={item.title}
				onClick={() =>
					navigate(Routing.Portfolio.FolderDetail.path(item.title))
				}
				rightSlot={
					<IconButton
						aria-label={t(Resources.Portfolio.List.FolderActionsDrawer.title)}
						onClick={handleOpenDrawer}
					>
						<EllipsisIcon
							size={theme.iconSize.sm}
							color={theme.palette.grey[400]}
						/>
					</IconButton>
				}
			/>

			<ActionsDrawerRedesign
				open={isDrawerOpen}
				onClose={() => setIsDrawerOpen(false)}
				title={t(Resources.Portfolio.List.FolderActionsDrawer.title)}
				concealed={isRenameDialogOpen}
				actions={actions}
				grabber
			/>

			<MyPlannDrawer
				open={isRenameDialogOpen}
				onClose={() => {
					setIsRenameDialogOpen(false);
					setIsDrawerOpen(false);
				}}
				title={t(
					Resources.Portfolio.List.DocumentActionsDrawer.Rename.folderTitle,
				)}
				grabber
			>
				<RenameFolderForm title={item.title} onSubmit={handleRename} />
			</MyPlannDrawer>
		</>
	);
};
